import {
  useFetchUnifiedFields,
  useGetUnifiedFieldsColumns,
} from '@modules/ManageUnifiedFields/hooks';
import { getUnifiedFieldsDataGridRows } from '@modules/ManageUnifiedFields/utils';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { trackManageUnifiedFieldsPageEvent } from '@modules/integrationsV2/utils/trackManageUnifiedFieldsPageEvent';
import { Box, Stack } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EditUnifiedFieldDrawer } from '../EditUnifiedFieldDrawer';

interface UnifiedFieldsDataGridProps {
  searchValue: string;
}

export const UnifiedFieldsDataGrid = ({
  searchValue,
}: UnifiedFieldsDataGridProps) => {
  const [openEditUnifiedFieldDrawer, setOpenEditUnifiedFieldDrawer] =
    useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState('');
  const { unifiedFields, loading } = useFetchUnifiedFields({
    searchValue,
  });

  const rows = getUnifiedFieldsDataGridRows(unifiedFields);
  const columns = useGetUnifiedFieldsColumns();

  const isAuthorized = useIsAuthorized({
    operation: PermissionOperation.Write,
    resource: PermissionResource.Metadata,
  });

  const handleRowClick = useCallback(
    (field: string) => {
      if (!isAuthorized) {
        return;
      }
      setOpenEditUnifiedFieldDrawer(true);
      setSelectedMetadata(field);
    },
    [isAuthorized],
  );

  const [searchParams] = useSearchParams();
  const metadataKey = searchParams.get('metadataKey');

  useEffect(() => {
    if (metadataKey) {
      handleRowClick(metadataKey);
    }

    trackManageUnifiedFieldsPageEvent({
      eventName: 'Manage Unified Field - Edit Unified Field Button Clicked',
    });
  }, [metadataKey, handleRowClick]);

  return (
    <Box
      sx={{
        height: '75vh',
        width: '100%',
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        hideFooter
        onRowClick={(params) => {
          handleRowClick(params.row.field);
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        disableRowSelectionOnClick
        showColumnVerticalBorder={false}
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No results to show.
            </Stack>
          ),
        }}
        loading={loading}
        sx={{
          border: 'none',
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row': {
            position: 'relative',
            '&: hover': {
              '.js-suggestion-row-action-button-group': {
                opacity: 1,
              },
            },
          },
        }}
        disableColumnFilter
        disableColumnMenu
      />
      {openEditUnifiedFieldDrawer && (
        <EditUnifiedFieldDrawer
          open={openEditUnifiedFieldDrawer}
          onClose={() => {
            setOpenEditUnifiedFieldDrawer(false);
          }}
          metadataKey={selectedMetadata}
        />
      )}
    </Box>
  );
};
